/**
 * <collection-vehicle-filter> - the garage-aware "only parts for your car" chip on collection pages.
 *
 * Rendered (empty) by blocks/collection-category-links.liquid as the first cell of the quick-links
 * row; everything it needs is client-side, so the server ships only a <template> skeleton and data
 * attributes. Three states:
 *
 *   - no saved vehicle  -> "Select your vehicle" chip that opens the header garage (data-open-garage)
 *   - vehicle, filter off -> "Only parts for your <car>" linking to the classic tag-filtered URL
 *                            /collections/<handle>/<tag-handle> (server-rendered, paginates correctly)
 *   - vehicle, filter on  -> active chip linking back to the unfiltered collection
 *
 * Turning the filter on sets a sticky flag (localStorage) so landing on any other category
 * collection re-applies it via location.replace - "site-wide" for shoppers who opted in, but with
 * the chip always present as the escape hatch. The chip never renders on vehicle collections
 * (already scoped to one car), and never auto-redirects when a different tag filter is active.
 *
 * Tag handles: lowercase, underscores kept, anything else becomes a hyphen (AUDI_8L -> audi_8l,
 * PORSCHE_991.1 -> porsche_991-1) - verified against live tag-filter URLs. The tag itself comes
 * from parts_finder_data.json (each vehicle's `tag`, sourced from its collection rule by
 * tools/build-parts-finder.mjs), never derived from make/chassis strings.
 */

import { getActive } from '@theme/garage-store';

const STICKY_KEY = 'revzone:fitment-filter';

let dataPromise = null;
function loadVehicles(url) {
  if (!dataPromise) {
    dataPromise = fetch(url)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => (d && Array.isArray(d.vehicles) ? d.vehicles : []))
      .catch(() => []);
  }
  return dataPromise;
}

function tagToHandle(tag) {
  return String(tag)
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function stickyOn() {
  try {
    return localStorage.getItem(STICKY_KEY) === '1';
  } catch {
    return false;
  }
}

function setSticky(on) {
  try {
    if (on) localStorage.setItem(STICKY_KEY, '1');
    else localStorage.removeItem(STICKY_KEY);
  } catch {
    /* private mode - the chip still works, it just won't persist */
  }
}

class CollectionVehicleFilter extends HTMLElement {
  connectedCallback() {
    this.collectionUrl = this.dataset.collectionUrl;
    this.collectionHandle = this.dataset.collectionHandle;
    this.currentTags = (this.dataset.currentTags || '')
      .split(',')
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);

    this.onVehicleChange = () => this.render();
    document.addEventListener('vehicle:change', this.onVehicleChange);

    // Fallback opener in case no section with the global data-open-garage binder is present.
    if (!window.__revzoneGarageOpenerBound) {
      window.__revzoneGarageOpenerBound = true;
      document.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-open-garage]');
        if (!opener) return;
        event.preventDefault();
        const triggers = document.querySelectorAll('vehicle-garage .vehicle-garage__trigger');
        const visible = Array.prototype.filter.call(triggers, (el) => el.offsetParent !== null)[0];
        (visible || triggers[0])?.click();
      });
    }

    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener('vehicle:change', this.onVehicleChange);
  }

  chip() {
    const template = this.querySelector('template');
    const node = template ? template.content.firstElementChild.cloneNode(true) : null;
    return node || document.createElement('a');
  }

  show(node) {
    this.replaceChildren(this.querySelector('template'), node);
    const item = this.closest('li');
    if (item) item.hidden = false;
    const nav = this.closest('nav');
    if (nav) nav.hidden = false;
  }

  clear() {
    this.replaceChildren(this.querySelector('template'));
    const item = this.closest('li');
    if (item) item.hidden = true;
  }

  async render() {
    const active = getActive();

    if (!active || !active.url) {
      const chip = this.chip();
      chip.href = '#';
      chip.setAttribute('data-open-garage', '');
      chip.querySelector('[data-label]').textContent = this.dataset.labelSelect;
      this.show(chip);
      return;
    }

    const vehicles = await loadVehicles(this.dataset.dataUrl);
    const mine = vehicles.find((v) => v.collection_url === active.url);

    // No fitment tag for this car, or we're on a vehicle collection (already one car's parts):
    // nothing useful to offer.
    if (!mine || !mine.tag || vehicles.some((v) => v.collection_handle === this.collectionHandle)) {
      this.clear();
      return;
    }

    const tagHandle = tagToHandle(mine.tag);
    const isFiltered = this.currentTags.includes(mine.tag.toLowerCase());
    const label = (this.dataset.labelOnly || '[vehicle]').replace('[vehicle]', active.label || mine.model_display);

    const chip = this.chip();
    chip.querySelector('[data-label]').textContent = label;

    if (isFiltered) {
      chip.href = this.collectionUrl + window.location.search;
      chip.classList.add('is-active');
      chip.setAttribute('aria-current', 'true');
      chip.insertAdjacentHTML(
        'beforeend',
        `<span class="collection-vehicle-filter__remove" aria-hidden="true">&times;</span>` +
          `<span class="visually-hidden">${this.dataset.labelClear}</span>`
      );
      chip.addEventListener('click', () => setSticky(false));
    } else {
      chip.href = `${this.collectionUrl}/${tagHandle}${window.location.search}`;
      chip.addEventListener('click', () => setSticky(true));

      // Sticky mode: shopper opted in earlier, so re-apply on this collection. Never fight an
      // explicit different tag filter, and replace() so back doesn't bounce through the redirect.
      if (stickyOn() && this.currentTags.length === 0) {
        window.location.replace(chip.href);
        return;
      }
    }

    this.show(chip);
  }
}

if (!customElements.get('collection-vehicle-filter')) {
  customElements.define('collection-vehicle-filter', CollectionVehicleFilter);
}
